import { useEffect, useId, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

/**
 * Erklärung zu einem Feld, hinter einem Fragezeichen am Label.
 *
 * Bewusst kein Mouse-over: erfasst wird am Handy an der Maschine, und dort gibt
 * es keinen Zeiger, der irgendwo verweilen könnte. Also ein antippbarer Knopf —
 * der funktioniert am Bildschirmrand genauso wie mit der Tastatur.
 *
 * Der Text ist reine Erklärung und darf verschwinden, sobald man weitertippt.
 * Deshalb schliesst er bei Esc und beim nächsten Klick irgendwohin, ohne dass
 * man den Knopf wiederfinden muss.
 */
export default function FieldHelp({ text }: { text: string }) {
  const { t } = useTranslation()
  const [isOpen, setIsOpen] = useState(false)
  const containerRef = useRef<HTMLSpanElement>(null)
  const id = useId()

  useEffect(() => {
    if (!isOpen) return

    function handlePointerDown(event: PointerEvent) {
      if (!containerRef.current?.contains(event.target as Node)) setIsOpen(false)
    }
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') setIsOpen(false)
    }

    document.addEventListener('pointerdown', handlePointerDown)
    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [isOpen])

  return (
    <span ref={containerRef} className="relative inline-flex">
      <button
        type="button"
        onClick={() => setIsOpen((open) => !open)}
        aria-expanded={isOpen}
        aria-controls={isOpen ? id : undefined}
        aria-label={t('common.help')}
        className="border-crema-300 dark:border-roast-600 text-roast-500 dark:text-crema-300/70 hover:bg-crema-100 dark:hover:bg-roast-700 flex size-5 items-center justify-center rounded-full border text-xs leading-none font-semibold"
      >
        ?
      </button>
      {isOpen && (
        <span
          id={id}
          role="note"
          // Rechts verankert: die Felder stehen im Raster oft am rechten Rand,
          // und ein nach links laufender Kasten bleibt dort im Bild.
          className="border-crema-300 dark:border-roast-600 dark:bg-roast-800 text-roast-700 dark:text-crema-200 absolute top-6 right-0 z-20 w-64 rounded-lg border bg-white p-3 text-xs leading-relaxed shadow-lg"
        >
          {text}
        </span>
      )}
    </span>
  )
}
